import { useState, useEffect, useCallback, useMemo } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Truck, Play, Stop, PencilSimple, Trash, Plus, Check, X, Warning, QrCode, Clock, User, Key, Printer, CaretLeft, CaretRight, CaretDown, Image, Camera, ShieldWarning, DownloadSimple, Copy } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { downloadQrKaart, copyQrKaart } from "@/lib/qrExport";
import { RittenBestuurderTabel } from "@/components/RittenBestuurderTabel";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
// Firmagegevens van de actieve firma (gevuld bij het laden)
const firmaInfo = { naam: "", adres: "", id: "" };

const VEHICLE_TYPES = [
  { value: "auto", label: "Personenwagen" }, { value: "bestelwagen", label: "Bestelwagen" },
  { value: "camion", label: "Camion" }, { value: "vrachtwagen", label: "Vrachtwagen" },
  { value: "aanhangwagen", label: "Aanhangwagen" }, { value: "overig", label: "Overig" },
];
const FUEL_TYPES = ["Diesel", "Benzine", "Elektrisch", "Hybride", "CNG/LPG"];

function formatDuur(min) { if (!min) return "0u 0m"; const h = Math.floor(min / 60); const m = Math.round(min % 60); return `${h}u ${m}m`; }
function isExpiringSoon(d) { if (!d) return false; return (new Date(d) - new Date()) / 86400000 < 30; }
function isExpired(d) { if (!d) return false; return new Date(d) < new Date(); }

const SUB_TABS = [
  { key: "actief", label: "Actieve ritten", icon: Play },
  { key: "voertuigen", label: "Voertuigen", icon: Truck },
  { key: "bestuurders", label: "Bestuurders", icon: User },
  { key: "overzicht", label: "Uren overzicht", icon: Clock },
  { key: "schade", label: "Schade overzicht", icon: ShieldWarning },
];

export default function WagenparkTab() {
  const [sub, setSub] = useState("actief");
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [medewerkers, setMedewerkers] = useState([]);
  const [activeRitten, setActiveRitten] = useState([]);
  const [allRitten, setAllRitten] = useState([]);
  const [stats, setStats] = useState({ by_driver: {}, by_vehicle: {}, total_ritten: 0 });
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const [vR, dR, aR, rR, sR, mR, bR] = await Promise.all([
        API.get("/vehicles").catch(() => ({ data: [] })),
        API.get("/drivers").catch(() => ({ data: [] })),
        API.get("/ritten/actief").catch(() => ({ data: [] })),
        API.get("/ritten").catch(() => ({ data: [] })),
        API.get("/ritten/stats").catch(() => ({ data: { by_driver: {}, by_vehicle: {}, total_ritten: 0 } })),
        API.get("/medewerkers").catch((e) => { toast.error("Medewerkers konden niet geladen worden — vernieuw de pagina"); console.error(e); return { data: [] }; }),
        API.get("/contracten/bedrijf").catch(() => ({ data: {} })),
      ]);
      firmaInfo.naam = bR.data?.naam || "";
      firmaInfo.adres = [bR.data?.adres, bR.data?.stad].filter(Boolean).join(" · ");
      firmaInfo.id = localStorage.getItem("hr_firma") || "";
      setVehicles(vR.data || []); setDrivers(dR.data || []);
      setActiveRitten(aR.data || []); setAllRitten(rR.data || []);
      setStats(sR.data || { by_driver: {}, by_vehicle: {}, total_ritten: 0 });
      setMedewerkers(mR.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Actieve ritten live houden: ritten gestart op een gsm verschijnen zo automatisch
  useEffect(() => {
    const verversActief = async () => {
      if (document.hidden) return;
      try {
        const r = await API.get("/ritten/actief");
        setActiveRitten(r.data || []);
      } catch { /* ignore */ }
    };
    const id = setInterval(verversActief, 15000);
    const opFocus = () => { verversActief(); };
    window.addEventListener("focus", opFocus);
    document.addEventListener("visibilitychange", opFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", opFocus);
      document.removeEventListener("visibilitychange", opFocus);
    };
  }, []);

  if (loading) return <div data-testid="wagenpark-tab" className="p-8 text-center text-steel-400 text-sm">Laden...</div>;

  return (
    <div data-testid="wagenpark-tab">
      <div className="flex gap-0 border-b border-steel-200 mb-4 overflow-x-auto">
        {SUB_TABS.map((t) => {
          const I = t.icon;
          return (
            <button key={t.key} data-testid={`wagenpark-sub-${t.key}`} onClick={() => setSub(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${sub === t.key ? "border-industrial text-steel-900" : "border-transparent text-steel-500 hover:text-steel-700"}`}>
              <I size={14} weight={sub === t.key ? "bold" : "regular"} /> {t.label}
              {t.key === "actief" && activeRitten.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-green-100 text-profit rounded-full">{activeRitten.length}</span>}
            </button>
          );
        })}
      </div>
      {sub === "actief" && <ActieveRitten vehicles={vehicles} drivers={drivers} activeRitten={activeRitten} onRefresh={loadAll} />}
      {sub === "voertuigen" && <VoertuigenTabel vehicles={vehicles} onRefresh={loadAll} />}
      {sub === "bestuurders" && <Bestuurders drivers={drivers} medewerkers={medewerkers} onRefresh={loadAll} />}
      {sub === "overzicht" && <UrenOverzicht stats={stats} allRitten={allRitten} drivers={drivers} onRefresh={loadAll} />}
      {sub === "schade" && <SchadeOverzicht allRitten={allRitten} vehicles={vehicles} />}
    </div>
  );
}

function ActieveRitten({ vehicles, drivers, activeRitten, onRefresh }) {
  const [selD, setSelD] = useState(""); const [selV, setSelV] = useState("");
  const [uploadingRid, setUploadingRid] = useState(null);
  const busy = new Set(activeRitten.map((r) => r.vehicle_vid));
  const avail = vehicles.filter((v) => !busy.has(v.vid));

  const start = async () => {
    const d = drivers.find((x) => x.did === selD), v = avail.find((x) => x.vid === selV);
    if (!d || !v) return;
    try {
      await API.post("/ritten/start", { driver_did: d.did, vehicle_vid: v.vid });
      toast.success(`Rit gestart - ${d.naam} / ${v.kenteken}`);
      setSelD(""); setSelV(""); onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rit starten mislukt");
    }
  };
  const stop = async (rid) => {
    try {
      await API.post(`/ritten/${rid}/stop`);
      toast.success("Rit gestopt");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rit stoppen mislukt");
    }
  };

  const uploadRitPhoto = async (rid, phase, e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingRid(rid);
    const fd = new FormData();
    Array.from(files).slice(0, 5).forEach((f) => fd.append("files", f));
    try {
      await API.post(`/trips/${rid}/photos?phase=${phase}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onRefresh();
    } catch { /* ignore */ }
    setUploadingRid(null);
    e.target.value = "";
  };

  return (
    <div>
      <div className="bg-white border border-steel-200 rounded-sm p-3 sm:p-5 mb-4">
        <h4 className="text-sm font-bold text-steel-900 mb-3 flex items-center gap-2"><Play size={16} weight="bold" className="text-profit" /> Rit starten</h4>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
            <label className="text-[10px] font-semibold text-steel-600">Bestuurder</label>
            <select data-testid="rit-select-driver" value={selD} onChange={(e) => setSelD(e.target.value)} className="px-3 py-2.5 border border-steel-200 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-steel-900">
              <option value="">-- Kies bestuurder --</option>
              {drivers.map((d) => <option key={d.did} value={d.did}>{d.naam}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
            <label className="text-[10px] font-semibold text-steel-600">Voertuig</label>
            <select data-testid="rit-select-vehicle" value={selV} onChange={(e) => setSelV(e.target.value)} className="px-3 py-2.5 border border-steel-200 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-steel-900">
              <option value="">-- Kies voertuig --</option>
              {avail.map((v) => <option key={v.vid} value={v.vid}>{v.kenteken} - {v.merk} {v.model}</option>)}
            </select>
          </div>
          <button data-testid="rit-start-btn" onClick={start} disabled={!selD || !selV}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-bold bg-profit text-white rounded-sm hover:bg-green-700 disabled:opacity-40 transition-colors">
            <Play size={16} weight="fill" /> START
          </button>
        </div>
      </div>
      {activeRitten.length === 0 ? (
        <div className="bg-white border border-steel-200 rounded-sm p-8 text-center text-steel-400 text-sm">Geen actieve ritten</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {activeRitten.map((r) => {
            const el = (new Date() - new Date(r.start_tijd)) / 60000;
            return (
              <div key={r.rid} data-testid={`active-rit-${r.rid}`} className="bg-white border-2 border-green-300 rounded-sm overflow-hidden">
                <div className="bg-green-600 px-4 py-2.5 flex items-center justify-between">
                  <div><div className="text-white font-bold text-sm">{r.driver_naam}</div><div className="text-green-200 text-[10px]">{r.vehicle_kenteken} - {r.vehicle_info}</div></div>
                  <div className="flex items-center gap-1 bg-green-700 px-2 py-1 rounded-sm"><div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" /><span className="text-white text-xs font-bold">ACTIEF</span></div>
                </div>
                <div className="p-4">
                  <div className="flex justify-between mb-2"><span className="text-steel-500 text-sm">Gestart</span><span className="font-mono text-sm font-bold">{new Date(r.start_tijd).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}</span></div>
                  <div className="flex justify-between mb-3"><span className="text-steel-500 text-sm">Duur</span><span className="font-mono text-lg font-black text-profit">{formatDuur(el)}</span></div>
                  <div className="flex gap-2 mb-3">
                    {r.fotos_start?.length > 0 && <span className="text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-sm">{r.fotos_start.length} start foto's</span>}
                    {r.fotos_eind?.length > 0 && <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-sm">{r.fotos_eind.length} eind foto's</span>}
                  </div>
                  <div className="flex gap-2 mb-3">
                    <label data-testid={`admin-photo-start-${r.rid}`} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold border border-steel-300 text-steel-600 rounded-sm hover:bg-steel-50 cursor-pointer transition-colors">
                      <Camera size={12} /> Start foto
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadRitPhoto(r.rid, "start", e)} />
                    </label>
                    <label data-testid={`admin-photo-eind-${r.rid}`} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold border border-amber-300 text-amber-700 rounded-sm hover:bg-amber-50 cursor-pointer transition-colors">
                      <Camera size={12} /> Eind foto
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadRitPhoto(r.rid, "eind", e)} />
                    </label>
                  </div>
                  {uploadingRid === r.rid && <div className="text-[10px] text-steel-400 mb-2 text-center">Uploaden...</div>}
                  <button data-testid={`rit-stop-${r.rid}`} onClick={() => stop(r.rid)} className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-bold bg-loss text-white rounded-sm hover:bg-red-700 transition-colors"><Stop size={16} weight="fill" /> STOP</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VoertuigenTabel({ vehicles, onRefresh }) {
  const [adding, setAdding] = useState(false); const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({}); const [confirmDel, setConfirmDel] = useState(null);
  const [qrVehicle, setQrVehicle] = useState(null);
  const empty = () => ({ kenteken: "", type: "auto", merk: "", model: "", bouwjaar: "", brandstof: "Diesel", km_stand: "", keuring_vervalt: "", verzekering_vervalt: "", notities: "" });
  const save = async () => {
    if (adding) await API.post("/vehicles", form); else if (editing) await API.put(`/vehicles/${editing}`, form);
    onRefresh(); setAdding(false); setEditing(null);
  };
  const remove = async (vid) => { await API.delete(`/vehicles/${vid}`); onRefresh(); setConfirmDel(null); };

  const qrUrl = qrVehicle ? `${window.location.origin}/rit?voertuig=${qrVehicle.vid}&firma=${qrVehicle.firma_id || firmaInfo.id}` : "";
  const printVehicleQr = () => {
    if (!qrVehicle) return;
    const svgEl = document.getElementById("veh-qr-svg")?.querySelector("svg");
    const typeLabel = VEHICLE_TYPES.find((t) => t.value === qrVehicle.type)?.label || qrVehicle.type || "";
    const sub = [qrVehicle.merk, qrVehicle.model, typeLabel].filter(Boolean).join(" · ");
    const html = `<html><head><meta charset="utf-8"/><style>
      body{margin:0;padding:20px;font-family:'IBM Plex Sans',Arial,sans-serif;color:#0F172A}
      .card{border:3px solid #0F172A;border-radius:4px;padding:40px 30px;text-align:center;max-width:360px;margin:0 auto}
      .logo{font-size:22px;font-weight:900;letter-spacing:-0.5px;margin-bottom:2px}
      .addr{font-size:9px;color:#94A3B8;letter-spacing:1px;margin-bottom:20px}
      .line{width:60px;height:1px;background:#CBD5E1;margin:0 auto 20px}
      .qr{margin:0 auto 20px;text-align:center}
      .plate{display:inline-block;font-size:22px;font-weight:900;letter-spacing:2px;padding:6px 14px;border:2px solid #0F172A;border-radius:4px;margin-bottom:8px}
      .sub{font-size:12px;color:#64748B;margin-bottom:8px}
      .hint{font-size:10px;color:#94A3B8;margin-bottom:16px}
      .url{font-size:8px;font-family:monospace;color:#CBD5E1;word-break:break-all}
      @media print{body{padding:0}@page{margin:15mm}}
    </style></head><body><div class="card">
      <div class="logo">${firmaInfo.naam}</div>
      <div class="addr">${firmaInfo.adres}</div>
      <div class="line"></div>
      <div class="qr">${svgEl?.outerHTML || ""}</div>
      <div class="plate">${qrVehicle.kenteken || "--"}</div>
      <div class="sub">${sub}</div>
      <div class="hint">Scan en voer uw PIN in om rit te starten</div>
      <div class="url">${qrUrl}</div>
    </div></body></html>`;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };
  const fields = [
    { k: "kenteken", l: "Kenteken" }, { k: "type", l: "Type", sel: VEHICLE_TYPES },
    { k: "merk", l: "Merk" }, { k: "model", l: "Model" },
    { k: "bouwjaar", l: "Bouwjaar" }, { k: "brandstof", l: "Brandstof", sel: FUEL_TYPES.map((f) => ({ value: f, label: f })) },
    { k: "km_stand", l: "KM" }, { k: "keuring_vervalt", l: "Keuring", t: "date" },
    { k: "verzekering_vervalt", l: "Verzekering", t: "date" }, { k: "notities", l: "Notities" }
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <span className="text-xs text-steel-400">{vehicles.length} voertuigen</span>
        <button data-testid="add-vehicle-btn" onClick={() => { setForm(empty()); setAdding(true); setEditing(null); }}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-steel-900 text-white rounded-sm hover:bg-steel-800"><Plus size={12} weight="bold" /> Voertuig</button>
      </div>
      {(adding || editing) && (
        <div className="bg-white border border-steel-200 rounded-sm p-4 mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            {fields.map(({ k, l, sel, t }) => (
              <div key={k} className="flex flex-col gap-0.5">
                <label className="text-[9px] font-semibold text-steel-500">{l}</label>
                {sel ? (
                  <select data-testid={`vehicle-form-${k}`} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="px-2 py-1 border border-steel-200 text-xs rounded-sm focus:outline-none focus:ring-1 focus:ring-steel-900">
                    {sel.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input data-testid={`vehicle-form-${k}`} type={t || "text"} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="px-2 py-1 border border-steel-200 text-xs rounded-sm focus:outline-none focus:ring-1 focus:ring-steel-900" />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid="save-vehicle" onClick={save} className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-profit text-white rounded-sm"><Check size={12} /> Opslaan</button>
            <button onClick={() => { setAdding(false); setEditing(null); }} className="px-3 py-1.5 text-xs font-semibold bg-steel-100 text-steel-600 rounded-sm">Annuleren</button>
          </div>
        </div>
      )}
      <div className="mob-list" data-testid="voertuigen-mobiel">
        {vehicles.map((v) => {
          const tl = VEHICLE_TYPES.find((t) => t.value === v.type)?.label || v.type;
          const ke = isExpired(v.keuring_vervalt); const kw = isExpiringSoon(v.keuring_vervalt);
          const ve = isExpired(v.verzekering_vervalt); const vw = isExpiringSoon(v.verzekering_vervalt);
          return (
            <div key={v.vid} className="mob-card" data-testid={`voertuig-kaart-${v.vid}`}>
              <div className="mob-card-head">
                <div className="min-w-0">
                  <p className="mob-title">{v.kenteken}</p>
                  <p className="text-xs text-steel-500 truncate">{[v.merk, v.model, tl].filter(Boolean).join(" · ")}</p>
                </div>
                <span className="mob-value shrink-0">{v.km_stand ? `${v.km_stand} km` : ""}</span>
              </div>
              <div className="mob-row mt-1.5">
                <span className="mob-label">Keuring</span>
                <span className={`mob-value ${ke ? "text-loss" : kw ? "text-amber-600" : ""}`}>{v.keuring_vervalt || "--"}</span>
              </div>
              <div className="mob-row">
                <span className="mob-label">Verzekering</span>
                <span className={`mob-value ${ve ? "text-loss" : vw ? "text-amber-600" : ""}`}>{v.verzekering_vervalt || "--"}</span>
              </div>
              <div className="flex justify-end gap-1.5 mt-2 pt-2 border-t border-steel-100">
                <button onClick={() => setQrVehicle(v)} className="p-2 border border-steel-200 rounded-sm text-steel-500" data-testid={`mob-qr-vehicle-${v.vid}`}><QrCode size={15} /></button>
                <button onClick={() => { setForm({ ...v }); setEditing(v.vid); setAdding(false); }} className="p-2 border border-steel-200 rounded-sm text-steel-500" data-testid={`mob-edit-vehicle-${v.vid}`}><PencilSimple size={15} /></button>
                <button onClick={() => remove(v.vid)} className="p-2 border border-steel-200 rounded-sm text-loss" data-testid={`mob-del-vehicle-${v.vid}`}><Trash size={15} /></button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="hidden md:block bg-white border border-steel-200 rounded-sm overflow-x-auto">
        <table className="w-full border-collapse">
          <thead><tr className="bg-steel-800">
            {["Kenteken", "Type", "Merk", "Model", "Bouwjaar", "Brandstof", "KM", "Keuring", "Verzekering", "Notities", ""].map((h, i) => (
              <th key={i} className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-steel-300">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {vehicles.map((v, i) => {
              const tl = VEHICLE_TYPES.find((t) => t.value === v.type)?.label || v.type;
              const kw = isExpiringSoon(v.keuring_vervalt); const ke = isExpired(v.keuring_vervalt);
              const vw = isExpiringSoon(v.verzekering_vervalt); const ve = isExpired(v.verzekering_vervalt);
              return (
                <tr key={v.vid} className={`border-b border-steel-100 hover:bg-steel-50/60 ${i % 2 === 0 ? "bg-white" : "bg-steel-50/30"}`}>
                  <td className="px-3 py-2 text-sm font-bold text-steel-900">{v.kenteken}</td>
                  <td className="px-3 py-2 text-xs text-steel-600">{tl}</td>
                  <td className="px-3 py-2 text-sm text-steel-700">{v.merk}</td>
                  <td className="px-3 py-2 text-sm text-steel-700">{v.model}</td>
                  <td className="px-3 py-2 text-xs text-steel-600">{v.bouwjaar}</td>
                  <td className="px-3 py-2 text-xs text-steel-600">{v.brandstof}</td>
                  <td className="px-3 py-2 text-xs tabular-nums text-steel-600">{v.km_stand}</td>
                  <td className={`px-3 py-2 text-xs ${ke ? "text-loss font-bold" : kw ? "text-amber-600 font-semibold" : "text-steel-600"}`}>{(ke || kw) && <Warning size={10} className="inline mr-0.5" />}{v.keuring_vervalt || "--"}</td>
                  <td className={`px-3 py-2 text-xs ${ve ? "text-loss font-bold" : vw ? "text-amber-600 font-semibold" : "text-steel-600"}`}>{(ve || vw) && <Warning size={10} className="inline mr-0.5" />}{v.verzekering_vervalt || "--"}</td>
                  <td className="px-3 py-2 text-xs text-steel-400 max-w-[120px] truncate">{v.notities}</td>
                  <td className="px-2 py-2"><div className="flex gap-0.5">
                    <button data-testid={`qr-vehicle-${v.vid}`} onClick={() => setQrVehicle(v)} className="p-1 text-steel-400 hover:text-industrial" title="QR code"><QrCode size={12} /></button>
                    <button onClick={() => { setForm({ ...v }); setEditing(v.vid); setAdding(false); }} className="p-1 text-steel-300 hover:text-steel-700"><PencilSimple size={12} /></button>
                    {confirmDel === v.vid ? (
                      <div className="flex gap-0.5">
                        <button onClick={() => remove(v.vid)} className="text-[9px] font-bold text-white bg-loss px-1 py-0.5 rounded-sm">Ja</button>
                        <button onClick={() => setConfirmDel(null)} className="text-[9px] text-steel-500 bg-steel-100 px-1 py-0.5 rounded-sm">Nee</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmDel(v.vid)} className="p-1 text-steel-300 hover:text-loss"><Trash size={12} /></button>
                    )}
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {qrVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setQrVehicle(null)}>
          <div className="bg-white rounded-sm p-6 max-w-sm w-full border-2 border-steel-200" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="border-2 border-steel-900 rounded-sm p-6 mb-4">
                <div className="text-xl font-black text-steel-900 tracking-tight mb-0.5">{firmaInfo.naam}</div>
                <div className="text-[9px] text-steel-400 tracking-wide mb-4">{firmaInfo.adres}</div>
                <div className="w-16 h-px bg-steel-200 mx-auto mb-4"></div>
                <div id="veh-qr-svg" className="flex justify-center mb-4"><QRCodeSVG value={qrUrl} size={220} level="M" includeMargin={true} /></div>
                <div className="inline-block px-3 py-1 border-2 border-steel-900 rounded-sm font-black text-lg tracking-[0.2em] text-steel-900 mb-2">{qrVehicle.kenteken || "--"}</div>
                <div className="text-sm text-steel-600 mb-1">{[qrVehicle.merk, qrVehicle.model].filter(Boolean).join(" ")}</div>
                <div className="text-[10px] text-steel-400 mb-3">Scan en voer PIN in om rit te starten</div>
                <div className="text-[9px] font-mono text-steel-300 break-all">{qrUrl}</div>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                <button data-testid="vehicle-qr-print" onClick={printVehicleQr} className="flex items-center gap-1 px-4 py-2 text-xs font-semibold bg-steel-900 text-white rounded-sm hover:bg-steel-800">
                  <Printer size={12} /> Print
                </button>
                <button data-testid="vehicle-qr-png" onClick={() => downloadQrKaart("veh-qr-svg", `qr-${qrVehicle.kenteken || qrVehicle.vid}`, { titel: firmaInfo.naam, subtitel: firmaInfo.adres, label: qrVehicle.kenteken || "", regels: [[qrVehicle.merk, qrVehicle.model].filter(Boolean).join(" "), "Scan en voer PIN in om rit te starten"] }).catch(() => alert("PNG maken mislukt"))} className="flex items-center gap-1 px-4 py-2 text-xs font-semibold border border-steel-300 text-steel-700 rounded-sm hover:bg-steel-50">
                  <DownloadSimple size={12} /> PNG
                </button>
                <button data-testid="vehicle-qr-copy" onClick={async () => { const ok = await copyQrKaart("veh-qr-svg", { titel: firmaInfo.naam, subtitel: firmaInfo.adres, label: qrVehicle.kenteken || "", regels: [[qrVehicle.merk, qrVehicle.model].filter(Boolean).join(" "), "Scan en voer PIN in om rit te starten"] }); alert(ok ? "QR code gekopieerd — plak hem in je labelprogramma" : "Kopiëren niet ondersteund in deze browser, gebruik PNG downloaden"); }} className="flex items-center gap-1 px-4 py-2 text-xs font-semibold border border-steel-300 text-steel-700 rounded-sm hover:bg-steel-50">
                  <Copy size={12} /> Kopieer
                </button>
                <button onClick={() => setQrVehicle(null)} className="px-4 py-2 text-xs font-semibold bg-steel-100 text-steel-600 rounded-sm">Sluiten</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Bestuurders({ drivers, medewerkers = [], onRefresh }) {
  const [adding, setAdding] = useState(false); const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({}); const [qrDriver, setQrDriver] = useState(null);
  const [showStdQr, setShowStdQr] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); const [pinForm, setPinForm] = useState(null); const [pin, setPin] = useState("");

  // Medewerker-ids die al aan een bestuurder gekoppeld zijn (exclusief de bestuurder die we bewerken)
  const linkedMwIds = useMemo(
    () => new Set(drivers.filter((d) => d.did !== editing && d.medewerker_id).map((d) => d.medewerker_id)),
    [drivers, editing]
  );

  const pickMedewerker = (mid) => {
    if (!mid) { setForm((f) => ({ ...f, medewerker_id: "", pin: "" })); return; }
    const m = medewerkers.find((x) => x.id === mid);
    if (!m) return;
    setForm((f) => ({
      ...f,
      medewerker_id: mid,
      naam: `${m.voornaam || ""} ${m.achternaam || ""}`.trim(),
      telefoon: m.telefoon || f.telefoon || "",
      pin: m.pincode || "",
    }));
  };

  const save = async () => {
    if (!form.naam || !form.naam.trim()) {
      toast.error("Naam is verplicht");
      return;
    }
    try {
      if (adding) await API.post("/drivers", form); else if (editing) await API.put(`/drivers/${editing}`, form);
      toast.success(adding ? "Bestuurder toegevoegd" : "Bestuurder bijgewerkt");
      onRefresh(); setAdding(false); setEditing(null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        toast.error("Niet geautoriseerd — log opnieuw in als Admin");
      } else {
        toast.error(e?.response?.data?.detail || "Opslaan mislukt");
      }
    }
  };
  const remove = async (did) => { await API.delete(`/drivers/${did}`); onRefresh(); setConfirmDel(null); };
  const savePin = async () => {
    try {
      await API.put(`/drivers/${pinForm}/pin`, { pin });
      toast.success("PIN ingesteld");
      setPinForm(null); setPin("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "PIN instellen mislukt");
    }
  };

  const qrUrl = qrDriver ? `${window.location.origin}/driver/${qrDriver.did}` : "";
  const stdQrUrl = `${window.location.origin}/rit${firmaInfo.id ? `?firma=${firmaInfo.id}` : ""}`;

  const printStdQr = () => {
    const svgEl = document.getElementById("std-qr-svg")?.querySelector("svg");
    const html = `<html><head><meta charset="utf-8"/><style>
      body{margin:0;padding:20px;font-family:'IBM Plex Sans',Arial,sans-serif;color:#0F172A}
      .card{border:3px solid #0F172A;border-radius:4px;padding:40px 30px;text-align:center;max-width:360px;margin:0 auto}
      .logo{font-size:22px;font-weight:900;letter-spacing:-0.5px;margin-bottom:2px}
      .addr{font-size:9px;color:#94A3B8;letter-spacing:1px;margin-bottom:20px}
      .line{width:60px;height:1px;background:#CBD5E1;margin:0 auto 20px}
      .qr{margin:0 auto 20px;text-align:center}
      .name{font-size:20px;font-weight:900;margin-bottom:4px}
      .sub{font-size:12px;color:#64748B;margin-bottom:8px}
      .hint{font-size:10px;color:#94A3B8;margin-bottom:16px}
      .url{font-size:8px;font-family:monospace;color:#CBD5E1;word-break:break-all}
      @media print{body{padding:0}@page{margin:15mm}}
    </style></head><body><div class="card">
      <div class="logo">${firmaInfo.naam}</div>
      <div class="addr">${firmaInfo.adres}</div>
      <div class="line"></div>
      <div class="qr">${svgEl?.outerHTML || ""}</div>
      <div class="name">RIT REGISTRATIE</div>
      <div class="sub">Scan deze QR code met uw telefoon</div>
      <div class="hint">Voer daarna uw persoonlijke PIN code in</div>
      <div class="url">${stdQrUrl}</div>
    </div></body></html>`;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <span className="text-xs text-steel-400">{drivers.length} bestuurders</span>
        <div className="flex gap-2">
          <button data-testid="std-qr-btn" onClick={() => setShowStdQr(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border border-industrial text-industrial rounded-sm hover:bg-orange-50 transition-colors">
            <QrCode size={12} weight="bold" /> Standaard QR
          </button>
          <button data-testid="add-driver-btn" onClick={() => { setForm({ naam: "", telefoon: "", rijbewijs: "", notities: "" }); setAdding(true); setEditing(null); }}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-steel-900 text-white rounded-sm hover:bg-steel-800"><Plus size={12} weight="bold" /> Bestuurder</button>
        </div>
      </div>
      {(adding || editing) && (
        <div className="bg-white border border-steel-200 rounded-sm p-4 mb-4">
          <div className="mb-3">
            <label className="text-[10px] font-semibold text-steel-600 block mb-1">
              Koppel medewerker (optioneel) — vult naam, telefoon en PIN automatisch in
            </label>
            <select
              data-testid="driver-form-medewerker"
              value={form.medewerker_id || ""}
              onChange={(e) => pickMedewerker(e.target.value)}
              className="w-full px-2 py-1.5 border border-steel-200 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-steel-900 bg-white"
            >
              <option value="">— Geen koppeling (externe chauffeur) —</option>
              {medewerkers.map((m) => {
                const alGekoppeld = linkedMwIds.has(m.id) && m.id !== form.medewerker_id;
                return (
                  <option key={m.id} value={m.id} disabled={alGekoppeld}>
                    {m.voornaam} {m.achternaam}{m.functie ? ` · ${m.functie}` : ""}
                    {alGekoppeld ? " · al bestuurder" : ""}
                  </option>
                );
              })}
            </select>
            {form.medewerker_id && form.pin && (
              <div className="text-[10px] text-profit mt-1 font-semibold">
                PIN {form.pin} wordt overgenomen van medewerker
              </div>
            )}
            {medewerkers.length === 0 && (
              <div className="text-[10px] text-industrial mt-1 font-semibold" data-testid="driver-form-geen-medewerkers">
                Geen medewerkers gevonden in deze firma — voeg ze eerst toe bij Medewerkers, of vul hieronder handmatig een externe chauffeur in.
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
            {[{ k: "naam", l: "Naam" }, { k: "telefoon", l: "Telefoon" }, { k: "rijbewijs", l: "Rijbewijs" }, { k: "notities", l: "Notities" }].map(({ k, l }) => (
              <div key={k} className="flex flex-col gap-1">
                <label className="text-[10px] font-semibold text-steel-600">{l}</label>
                <input data-testid={`driver-form-${k}`} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="px-2 py-1.5 border border-steel-200 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-steel-900" />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid="save-driver" onClick={save} className="flex items-center gap-1 px-4 py-2 text-xs font-semibold bg-profit text-white rounded-sm"><Check size={12} /> Opslaan</button>
            <button onClick={() => { setAdding(false); setEditing(null); }} className="px-4 py-2 text-xs font-semibold bg-steel-100 text-steel-600 rounded-sm">Annuleren</button>
          </div>
        </div>
      )}

      {qrDriver && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setQrDriver(null)}>
          <div className="bg-white rounded-sm max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div id="qr-print-area" className="p-8 text-center">
              <div className="border-2 border-steel-900 rounded-sm p-6">
                <div className="font-heading text-lg font-black tracking-tighter text-steel-900 mb-0.5">{firmaInfo.naam}</div>
                <div className="text-[9px] text-steel-400 tracking-wide mb-4">{firmaInfo.adres}</div>
                <div className="w-16 h-px bg-steel-200 mx-auto mb-4" />
                <div className="flex justify-center mb-4"><QRCodeSVG value={qrUrl} size={220} level="M" includeMargin={true} /></div>
                <div className="text-xl font-black text-steel-900 tracking-tight mb-1">{qrDriver.naam}</div>
                <div className="text-xs text-steel-500 mb-3">Scan deze QR code om uw rit te starten of stoppen</div>
                <div className="text-[9px] font-mono text-steel-300 break-all">{qrUrl}</div>
              </div>
            </div>
            <div className="px-8 pb-6 flex flex-wrap gap-2 justify-center">
              <button data-testid="print-qr-btn" onClick={() => {
                const area = document.getElementById("qr-print-area");
                const html = `<html><head><meta charset="utf-8"/><style>
                  body{margin:0;padding:20px;font-family:'IBM Plex Sans',Arial,sans-serif;color:#0F172A}
                  .card{border:3px solid #0F172A;border-radius:4px;padding:40px 30px;text-align:center;max-width:360px;margin:0 auto}
                  .logo{font-size:22px;font-weight:900;letter-spacing:-0.5px;margin-bottom:2px}
                  .addr{font-size:9px;color:#94A3B8;letter-spacing:1px;margin-bottom:20px}
                  .line{width:60px;height:1px;background:#CBD5E1;margin:0 auto 20px}
                  .qr{margin:0 auto 20px;text-align:center}
                  .qr svg{display:inline-block}
                  .name{font-size:24px;font-weight:900;margin-bottom:4px;letter-spacing:-0.5px}
                  .sub{font-size:11px;color:#64748B;margin-bottom:16px}
                  .url{font-size:8px;font-family:monospace;color:#CBD5E1;word-break:break-all}
                  @media print{body{padding:0}@page{margin:15mm}}
                </style></head><body><div class="card">
                  <div class="logo">${firmaInfo.naam}</div>
                  <div class="addr">${firmaInfo.adres}</div>
                  <div class="line"></div>
                  <div class="qr">${area.querySelector("svg")?.outerHTML || ""}</div>
                  <div class="name">${qrDriver.naam}</div>
                  <div class="sub">Scan deze QR code om uw rit te starten of stoppen</div>
                  <div class="url">${qrUrl}</div>
                </div></body></html>`;
                const win = window.open("", "_blank");
                win.document.write(html);
                win.document.close();
                setTimeout(() => win.print(), 400);
              }}
                className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold bg-steel-900 text-white rounded-sm hover:bg-steel-800 transition-colors">
                <Printer size={16} weight="bold" /> QR Code printen
              </button>
              <button data-testid="driver-qr-png" onClick={() => downloadQrKaart("qr-print-area", `qr-${(qrDriver.naam || "bestuurder").replace(/\s+/g, "-")}`, { titel: firmaInfo.naam, subtitel: firmaInfo.adres, label: "", regels: [qrDriver.naam, "Scan om uw rit te starten of stoppen"] }).catch(() => alert("PNG maken mislukt"))}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border border-steel-300 text-steel-700 rounded-sm hover:bg-steel-50">
                <DownloadSimple size={16} weight="bold" /> PNG
              </button>
              <button data-testid="driver-qr-copy" onClick={async () => { const ok = await copyQrKaart("qr-print-area", { titel: firmaInfo.naam, subtitel: firmaInfo.adres, label: "", regels: [qrDriver.naam, "Scan om uw rit te starten of stoppen"] }); alert(ok ? "QR code gekopieerd — plak hem in je labelprogramma" : "Kopiëren niet ondersteund in deze browser, gebruik PNG downloaden"); }}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border border-steel-300 text-steel-700 rounded-sm hover:bg-steel-50">
                <Copy size={16} weight="bold" /> Kopieer
              </button>
              <button onClick={() => setQrDriver(null)}
                className="px-5 py-2.5 text-sm font-semibold bg-steel-100 text-steel-600 rounded-sm hover:bg-steel-200 transition-colors">Sluiten</button>
            </div>
          </div>
        </div>
      )}

      {showStdQr && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowStdQr(false)}>
          <div className="bg-white rounded-sm max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div id="std-qr-svg" className="p-8 text-center">
              <div className="border-2 border-steel-900 rounded-sm p-6">
                <div className="font-heading text-lg font-black tracking-tighter text-steel-900 mb-0.5">{firmaInfo.naam}</div>
                <div className="text-[9px] text-steel-400 tracking-wide mb-4">{firmaInfo.adres}</div>
                <div className="w-16 h-px bg-steel-200 mx-auto mb-4" />
                <div className="flex justify-center mb-4"><QRCodeSVG value={stdQrUrl} size={220} level="M" includeMargin={true} /></div>
                <div className="text-lg font-black text-steel-900 tracking-tight mb-1">RIT REGISTRATIE</div>
                <div className="text-xs text-steel-500 mb-1">Scan deze QR code met uw telefoon</div>
                <div className="text-[10px] text-steel-400 mb-3">Voer daarna uw persoonlijke PIN code in</div>
                <div className="text-[9px] font-mono text-steel-300 break-all">{stdQrUrl}</div>
              </div>
            </div>
            <div className="px-8 pb-6 flex flex-wrap gap-2 justify-center">
              <button data-testid="print-std-qr-btn" onClick={printStdQr}
                className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold bg-steel-900 text-white rounded-sm hover:bg-steel-800 transition-colors">
                <Printer size={16} weight="bold" /> QR Code printen
              </button>
              <button data-testid="std-qr-png" onClick={() => downloadQrKaart("std-qr-svg", "qr-rit-registratie", { titel: firmaInfo.naam, subtitel: firmaInfo.adres, label: "", regels: ["RIT REGISTRATIE", "Scan en voer uw PIN code in"] }).catch(() => alert("PNG maken mislukt"))}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border border-steel-300 text-steel-700 rounded-sm hover:bg-steel-50">
                <DownloadSimple size={16} weight="bold" /> PNG
              </button>
              <button data-testid="std-qr-copy" onClick={async () => { const ok = await copyQrKaart("std-qr-svg", { titel: firmaInfo.naam, subtitel: firmaInfo.adres, label: "", regels: ["RIT REGISTRATIE", "Scan en voer uw PIN code in"] }); alert(ok ? "QR code gekopieerd — plak hem in je labelprogramma" : "Kopiëren niet ondersteund in deze browser, gebruik PNG downloaden"); }}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border border-steel-300 text-steel-700 rounded-sm hover:bg-steel-50">
                <Copy size={16} weight="bold" /> Kopieer
              </button>
              <button onClick={() => setShowStdQr(false)}
                className="px-5 py-2.5 text-sm font-semibold bg-steel-100 text-steel-600 rounded-sm hover:bg-steel-200 transition-colors">Sluiten</button>
            </div>
          </div>
        </div>
      )}

      {pinForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setPinForm(null)}>
          <div className="bg-white rounded-sm p-6 text-center max-w-xs" onClick={(e) => e.stopPropagation()}>
            <Key size={24} className="mx-auto text-steel-700 mb-2" />
            <h3 className="font-heading text-base font-bold text-steel-900 mb-3">PIN instellen</h3>
            <input data-testid="pin-input" type="password" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value)} placeholder="4-6 cijfers"
              className="w-full px-4 py-3 border border-steel-200 text-center text-2xl font-mono font-bold tracking-[0.5em] rounded-sm focus:outline-none focus:ring-2 focus:ring-steel-900 mb-4" />
            <div className="flex gap-2 justify-center">
              <button data-testid="save-pin" onClick={savePin} disabled={pin.length < 4} className="px-4 py-2 text-sm font-semibold bg-profit text-white rounded-sm disabled:opacity-40">Opslaan</button>
              <button onClick={() => setPinForm(null)} className="px-4 py-2 text-sm font-semibold bg-steel-100 text-steel-600 rounded-sm">Annuleren</button>
            </div>
          </div>
        </div>
      )}

      <div className="mob-list" data-testid="bestuurders-mobiel">
        {drivers.map((d) => {
          const mw = d.medewerker_id ? medewerkers.find((m) => m.id === d.medewerker_id) : null;
          return (
            <div key={d.did} className="mob-card" data-testid={`bestuurder-kaart-${d.did}`}>
              <div className="mob-card-head">
                <div className="min-w-0">
                  <p className="mob-title">{d.naam}</p>
                  <p className="text-xs text-steel-500 truncate">{[d.telefoon, d.rijbewijs].filter(Boolean).join(" · ") || "—"}</p>
                </div>
                {mw && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-industrial/10 text-industrial rounded-sm shrink-0">medewerker</span>}
              </div>
              <div className="flex justify-end gap-1.5 mt-2 pt-2 border-t border-steel-100">
                <button onClick={() => setQrDriver(d)} className="p-2 border border-steel-200 rounded-sm text-steel-500" data-testid={`mob-qr-driver-${d.did}`}><QrCode size={15} /></button>
                <button onClick={() => { setPinForm(d.did); setPin(""); }} className="p-2 border border-steel-200 rounded-sm text-steel-500" data-testid={`mob-pin-driver-${d.did}`}><Key size={15} /></button>
                <button onClick={() => { setForm({ ...d }); setEditing(d.did); setAdding(false); }} className="p-2 border border-steel-200 rounded-sm text-steel-500" data-testid={`mob-edit-driver-${d.did}`}><PencilSimple size={15} /></button>
                <button onClick={() => remove(d.did)} className="p-2 border border-steel-200 rounded-sm text-loss" data-testid={`mob-del-driver-${d.did}`}><Trash size={15} /></button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="hidden md:block bg-white border border-steel-200 rounded-sm overflow-hidden">
        <table className="w-full border-collapse">
          <thead><tr className="bg-steel-800">
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-steel-300">Naam</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-steel-300">Telefoon</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-steel-300">Rijbewijs</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-steel-300">Notities</th>
            <th className="w-36 px-2"></th>
          </tr></thead>
          <tbody>{drivers.map((d, i) => {
            const mw = d.medewerker_id ? medewerkers.find((m) => m.id === d.medewerker_id) : null;
            return (
            <tr key={d.did} className={`border-b border-steel-100 hover:bg-steel-50/60 ${i % 2 === 0 ? "bg-white" : "bg-steel-50/30"}`}>
              <td className="px-4 py-2.5 text-sm font-semibold text-steel-900">
                {d.naam}
                {mw && (
                  <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-industrial/10 text-industrial rounded-sm" title={`Gekoppeld aan medewerker ${mw.voornaam} ${mw.achternaam}`}>
                    medewerker
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 text-sm text-steel-600">{d.telefoon}</td>
              <td className="px-4 py-2.5 text-sm text-steel-600">{d.rijbewijs}</td>
              <td className="px-4 py-2.5 text-xs text-steel-400">{d.notities}</td>
              <td className="px-2 py-2.5"><div className="flex gap-1 justify-end">
                <button data-testid={`qr-driver-${d.did}`} onClick={() => setQrDriver(d)} className="p-1 text-steel-400 hover:text-industrial" title="QR"><QrCode size={14} /></button>
                <button data-testid={`pin-driver-${d.did}`} onClick={() => { setPinForm(d.did); setPin(""); }} className="p-1 text-steel-400 hover:text-steel-700" title="PIN"><Key size={14} /></button>
                <button onClick={() => { setForm({ ...d }); setEditing(d.did); setAdding(false); }} className="p-1 text-steel-300 hover:text-steel-700"><PencilSimple size={14} /></button>
                {confirmDel === d.did ? (
                  <div className="flex gap-0.5">
                    <button onClick={() => remove(d.did)} className="text-[9px] font-bold text-white bg-loss px-1.5 py-0.5 rounded-sm">Ja</button>
                    <button onClick={() => setConfirmDel(null)} className="text-[9px] text-steel-500 bg-steel-100 px-1.5 py-0.5 rounded-sm">Nee</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDel(d.did)} className="p-1 text-steel-300 hover:text-loss"><Trash size={14} /></button>
                )}
              </div></td>
            </tr>
            );
          })}</tbody>
        </table>
      </div>
    </div>
  );
}

const isoDag = (d) => {
  const dt = new Date(d);
  const tz = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000);
  return tz.toISOString().split("T")[0];
};


function UrenOverzicht({ stats, allRitten, drivers, onRefresh }) {
  const [filterDriver, setFilterDriver] = useState("");
  const [filterVoertuig, setFilterVoertuig] = useState("");
  const [photoModal, setPhotoModal] = useState(null);
  const [editRit, setEditRit] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);

  const [rangeMode, setRangeMode] = useState(false);
  const [datum, setDatum] = useState(() => isoDag(new Date()));
  const [datumVan, setDatumVan] = useState(() => isoDag(new Date()));
  const [datumTot, setDatumTot] = useState(() => isoDag(new Date()));
  const [openDagen, setOpenDagen] = useState([]);
  const [openDrivers, setOpenDrivers] = useState([]);

  const voertuigen = useMemo(
    () => [...new Set(allRitten.map((r) => r.vehicle_kenteken).filter(Boolean))].sort(),
    [allRitten],
  );

  const applyPreset = (k) => {
    const today = new Date();
    if (k === "today") {
      setRangeMode(false);
      setDatum(isoDag(today));
      return;
    }
    setRangeMode(true);
    if (k === "week") {
      const dow = (today.getDay() + 6) % 7;
      const start = new Date(today); start.setDate(today.getDate() - dow);
      setDatumVan(isoDag(start)); setDatumTot(isoDag(today));
    } else if (k === "last7") {
      const start = new Date(today); start.setDate(today.getDate() - 6);
      setDatumVan(isoDag(start)); setDatumTot(isoDag(today));
    } else if (k === "month") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      setDatumVan(isoDag(start)); setDatumTot(isoDag(today));
    } else if (k === "all") {
      setDatumVan("2020-01-01"); setDatumTot(isoDag(today));
    }
  };

  const filtered = useMemo(() => {
    let r = allRitten.filter((x) => x.status === "voltooid");
    if (filterDriver) r = r.filter((x) => x.driver_naam === filterDriver);
    if (filterVoertuig) r = r.filter((x) => x.vehicle_kenteken === filterVoertuig);
    r = r.filter((x) => {
      if (!x.start_tijd) return false;
      const d = isoDag(x.start_tijd);
      return rangeMode ? d >= datumVan && d <= datumTot : d === datum;
    });
    return r.sort((a, b) => (a.start_tijd < b.start_tijd ? 1 : -1));
  }, [allRitten, filterDriver, filterVoertuig, rangeMode, datum, datumVan, datumTot]);

  const heeftFilters = !!(filterDriver || filterVoertuig);

  const dagGroepen = useMemo(() => {
    const perDag = new Map();
    filtered.forEach((r) => {
      const d = isoDag(r.start_tijd);
      if (!perDag.has(d)) perDag.set(d, new Map());
      const dmap = perDag.get(d);
      const naam = r.driver_naam || "Onbekende bestuurder";
      if (!dmap.has(naam)) dmap.set(naam, []);
      dmap.get(naam).push(r);
    });
    return [...perDag.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([dag, dmap]) => {
        const bestuurders = [...dmap.entries()]
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([naam, ritten]) => ({
            naam,
            ritten,
            minuten: ritten.reduce((s, r) => s + (r.duur_minuten || 0), 0),
            voertuigen: [...new Set(ritten.map((r) => r.vehicle_kenteken))],
          }));
        const alle = bestuurders.flatMap((b) => b.ritten);
        return {
          dag,
          bestuurders,
          minuten: alle.reduce((s, r) => s + (r.duur_minuten || 0), 0),
          ritten: alle.length,
        };
      });
  }, [filtered]);

  useEffect(() => {
    if (dagGroepen.length === 1) {
      setOpenDagen([dagGroepen[0].dag]);
      setOpenDrivers(dagGroepen[0].bestuurders.map((b) => `${dagGroepen[0].dag}|${b.naam}`));
    } else {
      setOpenDagen([]);
      setOpenDrivers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dagGroepen.length, rangeMode, datum, datumVan, datumTot]);

  const toggleDag = (d) =>
    setOpenDagen((prev) => {
      if (prev.includes(d)) return prev.filter((x) => x !== d);
      const g = dagGroepen.find((x) => x.dag === d);
      if (g) setOpenDrivers((f) => [...new Set([...f, ...g.bestuurders.map((b) => `${d}|${b.naam}`)])]);
      return [...prev, d];
    });
  const toggleDriver = (key) =>
    setOpenDrivers((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));

  const dagLabel = (d) => {
    const dt = new Date(`${d}T00:00:00`);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("nl-BE", { weekday: "long", day: "numeric", month: "long" });
  };

  const startEditRit = (r) => {
    setEditRit(r.rid);
    setEditForm({
      start_tijd: r.start_tijd ? r.start_tijd.slice(0, 16) : "",
      eind_tijd: r.eind_tijd ? r.eind_tijd.slice(0, 16) : "",
    });
  };

  const saveEditRit = async () => {
    try {
      const payload = {};
      if (editForm.start_tijd) payload.start_tijd = new Date(editForm.start_tijd).toISOString();
      if (editForm.eind_tijd) payload.eind_tijd = new Date(editForm.eind_tijd).toISOString();
      await API.put(`/ritten/${editRit}`, payload);
      setEditRit(null);
      if (onRefresh) onRefresh();
    } catch { /* ignore */ }
  };

  const deleteRit = async (rid) => {
    try {
      await API.delete(`/ritten/${rid}`);
      setConfirmDel(null);
      if (onRefresh) onRefresh();
    } catch { /* ignore */ }
  };

  const printPDF = () => {
    const rows = filtered;
    const driverLabel = filterDriver || "Alle bestuurders";
    const periode = rangeMode ? `${datumVan} t/m ${datumTot}` : datum;
    let html = `<html><head><meta charset="utf-8"/><style>
      body{font-family:'IBM Plex Sans',Arial,sans-serif;font-size:10px;margin:30px;color:#0F172A}
      h1{font-size:16px;font-weight:900;margin:0 0 4px}
      .sub{font-size:9px;color:#64748B;margin-bottom:15px}
      table{width:100%;border-collapse:collapse}
      th{background:#1E293B;color:#fff;padding:5px 8px;font-size:8px;text-align:left;text-transform:uppercase;border:1px solid #1E293B}
      td{padding:4px 8px;border:1px solid #CBD5E1;font-size:9px}
      .sub-tot td{background:#F1F5F9;font-weight:bold}
      .dag-tot td{background:#E2E8F0;font-weight:900;border-top:2px solid #1E293B}
      .tot td{background:#1E293B;color:#fff;font-weight:bold}
      .r{text-align:right}
    </style></head><body>
    <h1>${firmaInfo.naam} - Uren Rapport</h1>
    <div class="sub">Bestuurder: ${driverLabel} | Periode: ${periode} | ${rows.length} ritten</div>
    <table><thead><tr><th>Dag</th><th>Bestuurder</th><th>Voertuig</th><th>Start</th><th>Einde</th><th class="r">Duur</th></tr></thead><tbody>`;
    const klok = (iso) => (iso
      ? new Date(iso).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })
      : "--:--");
    let totMin = 0;
    dagGroepen.forEach((g) => {
      g.bestuurders.forEach((b) => {
        b.ritten.forEach((r) => {
          totMin += r.duur_minuten || 0;
          html += `<tr><td>${g.dag}</td><td>${r.driver_naam}</td><td>${r.vehicle_kenteken}</td><td>${klok(r.start_tijd)}</td><td>${klok(r.eind_tijd)}</td><td class="r"><b>${formatDuur(r.duur_minuten)}</b></td></tr>`;
        });
        html += `<tr class="sub-tot"><td colspan="5">Subtotaal ${b.naam} · ${b.ritten.length} rit(ten)</td><td class="r">${formatDuur(b.minuten)}</td></tr>`;
      });
      html += `<tr class="dag-tot"><td colspan="5">Dagtotaal ${dagLabel(g.dag)} · ${g.bestuurders.length} bestuurder(s) · ${g.ritten} rit(ten)</td><td class="r">${formatDuur(g.minuten)}</td></tr>`;
    });
    html += `</tbody><tfoot><tr class="tot"><td colspan="5">TOTAAL · ${rows.length} rit(ten)</td><td class="r">${formatDuur(totMin)}</td></tr></tfoot></table></body></html>`;
    const win = window.open("", "_blank"); win.document.write(html); win.document.close(); setTimeout(() => win.print(), 500);
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-white border border-steel-200 rounded-sm p-4 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wider text-steel-400 mb-1">Totaal ritten</div>
          <div className="text-2xl font-black text-steel-900 tabular-nums">{stats.total_ritten}</div>
        </div>
        <div className="bg-white border border-steel-200 rounded-sm p-4 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wider text-steel-400 mb-1">Totaal uren</div>
          <div className="text-2xl font-black text-profit tabular-nums">{formatDuur(Object.values(stats.by_driver).reduce((a, b) => a + b, 0))}</div>
        </div>
        <div className="bg-white border border-steel-200 rounded-sm p-4 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wider text-steel-400 mb-1">Bestuurders</div>
          <div className="text-2xl font-black text-steel-900 tabular-nums">{Object.keys(stats.by_driver).length}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-steel-200 rounded-sm overflow-hidden">
          <div className="px-4 py-2.5 bg-steel-50 border-b border-steel-200 text-[10px] font-bold text-steel-700 uppercase tracking-wider">Per bestuurder</div>
          <table className="w-full"><tbody>
            {Object.entries(stats.by_driver).sort((a, b) => b[1] - a[1]).map(([n, m], i) => (
              <tr key={n} className={`border-b border-steel-100 ${i % 2 === 0 ? "bg-white" : "bg-steel-50/30"}`}>
                <td className="px-4 py-2 text-sm font-semibold text-steel-900">{n}</td>
                <td className="px-4 py-2 text-sm text-right font-mono font-bold text-profit tabular-nums">{formatDuur(m)}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <div className="bg-white border border-steel-200 rounded-sm overflow-hidden">
          <div className="px-4 py-2.5 bg-steel-50 border-b border-steel-200 text-[10px] font-bold text-steel-700 uppercase tracking-wider">Per voertuig</div>
          <table className="w-full"><tbody>
            {Object.entries(stats.by_vehicle).sort((a, b) => b[1] - a[1]).map(([k, m], i) => (
              <tr key={k} className={`border-b border-steel-100 ${i % 2 === 0 ? "bg-white" : "bg-steel-50/30"}`}>
                <td className="px-4 py-2 text-sm font-mono font-semibold text-steel-900">{k}</td>
                <td className="px-4 py-2 text-sm text-right font-mono font-bold text-profit tabular-nums">{formatDuur(m)}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      </div>

      {/* Periode + presets */}
      <div className="bg-white border border-steel-200 rounded-sm p-3 mb-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          {[
            { k: "today", l: "Vandaag" },
            { k: "week", l: "Deze week" },
            { k: "last7", l: "Laatste 7 d" },
            { k: "month", l: "Deze maand" },
            { k: "all", l: "Alles" },
          ].map((p) => (
            <button key={p.k} onClick={() => applyPreset(p.k)}
              className="text-[11px] font-semibold px-2 py-1.5 rounded-sm border border-steel-200 hover:bg-steel-900 hover:text-white hover:border-steel-900 transition-colors"
              data-testid={`rit-preset-${p.k}`}>
              {p.l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="rit-range-toggle">
            <input type="checkbox" checked={rangeMode} onChange={(e) => setRangeMode(e.target.checked)} className="accent-steel-900" />
            <span className="font-semibold">Periode</span>
          </label>
          {rangeMode ? (
            <>
              <input type="date" value={datumVan} onChange={(e) => setDatumVan(e.target.value)} className="px-2 h-8 text-xs border border-steel-200 rounded-sm" data-testid="rit-range-van" />
              <span className="text-steel-400 text-xs">tot</span>
              <input type="date" value={datumTot} onChange={(e) => setDatumTot(e.target.value)} className="px-2 h-8 text-xs border border-steel-200 rounded-sm" data-testid="rit-range-tot" />
            </>
          ) : (
            <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className="px-2 h-8 text-xs border border-steel-200 rounded-sm" data-testid="rit-range-single" />
          )}
        </div>
      </div>

      {/* Filters + acties */}
      <div className="bg-white border border-steel-200 rounded-sm p-3 mb-3 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[160px]">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-steel-400 block mb-1">Bestuurder</label>
          <select data-testid="filter-driver" value={filterDriver} onChange={(e) => setFilterDriver(e.target.value)}
            className="w-full px-2 h-8 text-sm border border-steel-200 rounded-sm focus:outline-none">
            <option value="">Alle bestuurders</option>
            {drivers.map((d) => <option key={d.did} value={d.naam}>{d.naam}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-steel-400 block mb-1">Voertuig</label>
          <select data-testid="filter-voertuig" value={filterVoertuig} onChange={(e) => setFilterVoertuig(e.target.value)}
            className="w-full px-2 h-8 text-sm border border-steel-200 rounded-sm focus:outline-none">
            <option value="">Alle voertuigen</option>
            {voertuigen.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        {heeftFilters && (
          <button type="button" onClick={() => { setFilterDriver(""); setFilterVoertuig(""); }}
            className="h-8 px-2 text-xs text-steel-500 hover:text-steel-900 underline" data-testid="rit-clear-filters">
            Wis filters
          </button>
        )}
        <div className="ml-auto flex gap-2">
          <button data-testid="print-uren-pdf" onClick={printPDF}
            className="flex items-center gap-1 px-3 h-8 text-xs font-semibold bg-steel-900 text-white rounded-sm hover:bg-steel-800">
            <Printer size={13} /> Print / PDF
          </button>
        </div>
      </div>

      {/* Inklapbaar: per dag -> per bestuurder */}
      {dagGroepen.length === 0 ? (
        <div className="bg-white border border-steel-200 rounded-sm p-10 text-center text-sm text-steel-400" data-testid="rit-leeg">
          Geen ritten in deze periode
        </div>
      ) : (
        <div className="space-y-2" data-testid="ritten-accordeon">
          {dagGroepen.length > 1 && (
            <div className="flex items-center justify-end gap-2">
              <button type="button" data-testid="rit-alles-openklappen"
                onClick={() => { setOpenDagen(dagGroepen.map((g) => g.dag)); setOpenDrivers(dagGroepen.flatMap((g) => g.bestuurders.map((b) => `${g.dag}|${b.naam}`))); }}
                className="text-[11px] font-semibold text-steel-900 underline">Alles openklappen</button>
              <span className="text-steel-300 text-[11px]">·</span>
              <button type="button" data-testid="rit-alles-dichtklappen"
                onClick={() => { setOpenDagen([]); setOpenDrivers([]); }}
                className="text-[11px] font-semibold text-steel-400 underline">Alles dichtklappen</button>
            </div>
          )}
          {dagGroepen.map((g) => {
            const dagOpen = openDagen.includes(g.dag);
            return (
              <div key={g.dag} className="bg-white border border-steel-200 rounded-sm overflow-hidden" data-testid={`rit-dag-${g.dag}`}>
                <button type="button" onClick={() => toggleDag(g.dag)} data-testid={`rit-dag-toggle-${g.dag}`}
                  className="w-full flex items-center gap-2 px-3 py-3 text-left md:hover:bg-steel-50 transition-colors">
                  {dagOpen ? <CaretDown size={16} weight="bold" className="text-steel-900 shrink-0" /> : <CaretRight size={16} weight="bold" className="text-steel-400 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-black capitalize truncate text-steel-900">{dagLabel(g.dag)}</p>
                    <p className="text-[11px] text-steel-400 truncate">
                      {g.bestuurders.length} bestuurder{g.bestuurders.length === 1 ? "" : "s"} · {g.ritten} rit{g.ritten === 1 ? "" : "ten"} · {formatDuur(g.minuten)}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {g.bestuurders.map((b) => (
                        <span key={b.naam} className="text-[10px] font-semibold bg-steel-100 text-steel-700 rounded-full px-2 py-0.5">
                          {b.naam} · {formatDuur(b.minuten)}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>

                {dagOpen && (
                  <div className="border-t border-steel-200" data-testid={`rit-dag-inhoud-${g.dag}`}>
                    {g.bestuurders.map((b) => {
                      const key = `${g.dag}|${b.naam}`;
                      const open = openDrivers.includes(key);
                      return (
                        <div key={key} className="border-b border-steel-200 last:border-b-0">
                          <button type="button" onClick={() => toggleDriver(key)} data-testid={`rit-driver-toggle-${key}`}
                            className="w-full flex items-center gap-2 px-3 py-2 bg-steel-50 text-left md:hover:bg-steel-100 transition-colors">
                            {open ? <CaretDown size={13} weight="bold" className="shrink-0" /> : <CaretRight size={13} weight="bold" className="shrink-0 text-steel-400" />}
                            <User size={14} weight="bold" className="text-steel-900 shrink-0" />
                            <span className="text-xs font-bold uppercase tracking-wider truncate flex-1 text-steel-900">{b.naam}</span>
                            <span className="text-[11px] font-bold tabular-nums text-steel-500 shrink-0">
                              {b.ritten.length} · {formatDuur(b.minuten)}
                            </span>
                          </button>

                          {open && (
                            <RittenBestuurderTabel
                              bestuurder={b}
                              onEdit={startEditRit}
                              onDeleteVraag={setConfirmDel}
                              onDelete={deleteRit}
                              confirmDel={confirmDel}
                              onFotos={setPhotoModal}
                            />
                          )}
                        </div>
                      );
                    })}
                    <div
                      className="flex items-center gap-3 px-3 py-2.5 border-t-2 border-steel-300 bg-steel-100 text-xs font-bold"
                      data-testid={`rit-dagtotaal-${g.dag}`}
                    >
                      <span className="uppercase tracking-wider text-steel-900">Dagtotaal</span>
                      <span className="text-steel-500 font-semibold">
                        {g.bestuurders.length} bestuurder{g.bestuurders.length === 1 ? "" : "s"} · {g.ritten} rit{g.ritten === 1 ? "" : "ten"}
                      </span>
                      <span className="ml-auto tabular-nums text-sm font-black font-mono">{formatDuur(g.minuten)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editRit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditRit(null)}>
          <div className="bg-white rounded-sm p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-base font-bold text-steel-900 mb-4">Rit bewerken</h3>
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-steel-400 mb-1 block">Starttijd</label>
                <input data-testid="edit-rit-start" type="datetime-local" value={editForm.start_tijd}
                  onChange={(e) => setEditForm((f) => ({ ...f, start_tijd: e.target.value }))}
                  className="w-full px-3 py-2 border border-steel-200 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-steel-900" />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-steel-400 mb-1 block">Eindtijd</label>
                <input data-testid="edit-rit-eind" type="datetime-local" value={editForm.eind_tijd}
                  onChange={(e) => setEditForm((f) => ({ ...f, eind_tijd: e.target.value }))}
                  className="w-full px-3 py-2 border border-steel-200 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-steel-900" />
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="save-edit-rit" onClick={saveEditRit}
                className="px-4 py-2 text-sm font-semibold bg-profit text-white rounded-sm hover:bg-green-700 transition-colors">Opslaan</button>
              <button onClick={() => setEditRit(null)}
                className="px-4 py-2 text-sm font-semibold bg-steel-100 text-steel-600 rounded-sm hover:bg-steel-200 transition-colors">Annuleren</button>
            </div>
          </div>
        </div>
      )}

      {photoModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setPhotoModal(null)}>
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-base font-bold text-steel-900">
                Rit foto's - {photoModal.driver_naam} ({photoModal.vehicle_kenteken})
              </h3>
              <button onClick={() => setPhotoModal(null)} className="p-1 text-steel-400 hover:text-steel-700"><X size={18} /></button>
            </div>
            {photoModal.fotos_start?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs font-bold text-steel-700 uppercase tracking-wider mb-2">Start rit (staat voertuig)</h4>
                <div className="flex flex-wrap gap-2">
                  {photoModal.fotos_start.map((url, i) => (
                    <a key={i} href={`${API_BASE}${url}`} target="_blank" rel="noopener noreferrer" className="block w-32 h-32 rounded-sm overflow-hidden border border-steel-200 hover:border-steel-400 transition-colors">
                      <img src={`${API_BASE}${url}`} alt={`Start ${i + 1}`} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            {photoModal.fotos_eind?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs font-bold text-loss uppercase tracking-wider mb-2">Einde rit (schade registratie)</h4>
                <div className="flex flex-wrap gap-2">
                  {photoModal.fotos_eind.map((url, i) => (
                    <a key={i} href={`${API_BASE}${url}`} target="_blank" rel="noopener noreferrer" className="block w-32 h-32 rounded-sm overflow-hidden border border-red-200 hover:border-red-400 transition-colors">
                      <img src={`${API_BASE}${url}`} alt={`Einde ${i + 1}`} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            {!photoModal.fotos_start?.length && !photoModal.fotos_eind?.length && (
              <p className="text-sm text-steel-400 text-center py-4">Geen foto's voor deze rit</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SchadeOverzicht({ allRitten, vehicles }) {
  const [selVehicle, setSelVehicle] = useState("all");
  const [expandedRit, setExpandedRit] = useState(null);

  const rittenMetFotos = useMemo(() => {
    return allRitten
      .filter((r) => (r.fotos_start?.length > 0 || r.fotos_eind?.length > 0))
      .filter((r) => selVehicle === "all" || r.vehicle_vid === selVehicle)
      .sort((a, b) => new Date(b.start_tijd) - new Date(a.start_tijd));
  }, [allRitten, selVehicle]);

  const vehiclesWithPhotos = useMemo(() => {
    const vids = new Set();
    allRitten.forEach((r) => {
      if (r.fotos_start?.length > 0 || r.fotos_eind?.length > 0) vids.add(r.vehicle_vid);
    });
    return vehicles.filter((v) => vids.has(v.vid));
  }, [allRitten, vehicles]);

  const totalPhotos = useMemo(() => {
    let start = 0, eind = 0;
    rittenMetFotos.forEach((r) => {
      start += r.fotos_start?.length || 0;
      eind += r.fotos_eind?.length || 0;
    });
    return { start, eind, total: start + eind };
  }, [rittenMetFotos]);

  return (
    <div data-testid="schade-overzicht">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-white border border-steel-200 rounded-sm p-3 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wider text-steel-400 mb-1">Ritten met foto's</div>
          <div className="text-2xl font-black text-steel-900 tabular-nums">{rittenMetFotos.length}</div>
        </div>
        <div className="bg-white border border-steel-200 rounded-sm p-3 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wider text-steel-400 mb-1">Totaal foto's</div>
          <div className="text-2xl font-black text-industrial tabular-nums">{totalPhotos.total}</div>
        </div>
        <div className="bg-white border border-steel-200 rounded-sm p-3 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wider text-steel-400 mb-1">Start foto's</div>
          <div className="text-2xl font-black text-profit tabular-nums">{totalPhotos.start}</div>
        </div>
        <div className="bg-white border border-steel-200 rounded-sm p-3 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wider text-steel-400 mb-1">Schade foto's</div>
          <div className="text-2xl font-black text-loss tabular-nums">{totalPhotos.eind}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-steel-500">Voertuig:</span>
        <button data-testid="schade-filter-all" onClick={() => setSelVehicle("all")}
          className={`px-3 py-1.5 text-xs font-medium border rounded-sm transition-colors ${selVehicle === "all" ? "bg-steel-900 text-white border-steel-900" : "bg-white text-steel-600 border-steel-200 hover:bg-steel-50"}`}>
          Alle ({rittenMetFotos.length})
        </button>
        {vehiclesWithPhotos.map((v) => {
          const count = allRitten.filter((r) => r.vehicle_vid === v.vid && (r.fotos_start?.length > 0 || r.fotos_eind?.length > 0)).length;
          return (
            <button key={v.vid} data-testid={`schade-filter-${v.vid}`} onClick={() => setSelVehicle(v.vid)}
              className={`px-3 py-1.5 text-xs font-medium border rounded-sm transition-colors ${selVehicle === v.vid ? "bg-steel-900 text-white border-steel-900" : "bg-white text-steel-600 border-steel-200 hover:bg-steel-50"}`}>
              {v.kenteken} ({count})
            </button>
          );
        })}
      </div>

      {rittenMetFotos.length === 0 ? (
        <div className="bg-white border border-steel-200 rounded-sm p-10 text-center text-steel-400 text-sm">
          Geen ritten met foto's gevonden
        </div>
      ) : (
        <div className="space-y-3">
          {rittenMetFotos.map((r) => {
            const isExpanded = expandedRit === r.rid;
            const startDate = r.start_tijd ? new Date(r.start_tijd) : null;
            const endDate = r.eind_tijd ? new Date(r.eind_tijd) : null;
            const startCount = r.fotos_start?.length || 0;
            const eindCount = r.fotos_eind?.length || 0;

            return (
              <div key={r.rid} data-testid={`schade-rit-${r.rid}`} className="bg-white border border-steel-200 rounded-sm overflow-hidden">
                <button onClick={() => setExpandedRit(isExpanded ? null : r.rid)}
                  className="w-full text-left px-4 py-3 flex items-center gap-4 hover:bg-steel-50/50 transition-colors">
                  <div className="flex-shrink-0 w-20">
                    <div className="text-xs font-mono font-bold text-steel-900">
                      {startDate?.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                    </div>
                    <div className="text-[10px] font-mono text-steel-400">
                      {startDate?.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}
                      {endDate && ` - ${endDate.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}`}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="text-sm font-bold text-steel-900">{r.vehicle_kenteken}</div>
                    <div className="text-[10px] text-steel-500">{r.vehicle_info}</div>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="text-sm font-semibold text-steel-700">{r.driver_naam}</div>
                    <div className="text-[10px] text-steel-400">{formatDuur(r.duur_minuten)}</div>
                  </div>
                  <div className="ml-auto flex gap-2">
                    {startCount > 0 && <span className="text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-sm">{startCount} start</span>}
                    {eindCount > 0 && <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-sm">{eindCount} eind</span>}
                    <CaretRight size={14} className={`text-steel-400 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t border-steel-200 px-4 py-4 bg-steel-50/30">
                    {r.fotos_start?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-[10px] font-bold text-profit uppercase tracking-wider mb-2 flex items-center gap-1">
                          <Camera size={12} /> Start rit - Staat voertuig
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {r.fotos_start.map((url, i) => (
                            <a key={i} href={`${API_BASE}${url}`} target="_blank" rel="noopener noreferrer"
                              className="block w-36 h-36 rounded-sm overflow-hidden border-2 border-green-200 hover:border-green-400 transition-colors shadow-sm">
                              <img src={`${API_BASE}${url}`} alt={`Start ${i + 1}`} className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {r.fotos_eind?.length > 0 && (
                      <div>
                        <h4 className="text-[10px] font-bold text-loss uppercase tracking-wider mb-2 flex items-center gap-1">
                          <ShieldWarning size={12} /> Einde rit - Schade registratie
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {r.fotos_eind.map((url, i) => (
                            <a key={i} href={`${API_BASE}${url}`} target="_blank" rel="noopener noreferrer"
                              className="block w-36 h-36 rounded-sm overflow-hidden border-2 border-red-200 hover:border-red-400 transition-colors shadow-sm">
                              <img src={`${API_BASE}${url}`} alt={`Einde ${i + 1}`} className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
